import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Gem } from 'lucide-react';

const CreditsButton = styled.button<{ $isLow: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: ${props => props.$isLow 
    ? 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)' 
    : 'linear-gradient(135deg, #667eea 0%, #764ba2ff 100%)'};
  border: none;
  border-radius: 20px;
  color: white;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.1);
    transform: translateX(-100%);
    transition: transform 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${props => props.$isLow 
      ? 'rgba(255, 107, 107, 0.4)' 
      : 'rgba(102, 126, 234, 0.4)'};
    
    &::before {
      transform: translateX(0);
    }
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    padding: 10px 16px;
    font-size: 15px;
    min-height: 44px; // Accessibility - minimum touch target
  }
  
  ${props => props.$isLow && `
    animation: pulse 2s infinite;
    
    @keyframes pulse {
      0%, 100% { 
        box-shadow: 0 2px 8px rgba(255, 107, 107, 0.4);
      }
      50% { 
        box-shadow: 0 2px 16px rgba(255, 107, 107, 0.6);
      }
    }
  `}
`;

const CreditsAmount = styled.span`
  font-size: 14px;
  letter-spacing: 0.5px;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const PlusIcon = styled.span`
  margin-left: 2px;
  opacity: 0.9;
  font-size: 14px;
  font-weight: 700;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

interface CreditsDisplayProps {
  credits: number;
  onPurchaseClick: () => void;
}

export const CreditsDisplay: React.FC<CreditsDisplayProps> = ({ 
  credits, 
  onPurchaseClick 
}) => {
  const { t } = useTranslation();
  const isLow = credits < 5;
  
  return (
    <CreditsButton
      onClick={onPurchaseClick}
      $isLow={isLow}
      aria-label={`${credits} ${t('credits.label')}. ${t('credits.clickToBuy')}`}
      title={isLow ? t('credits.lowCreditsTooltip') : t('credits.clickToBuy')}
    >
      <Gem aria-hidden="true"/>
      <CreditsAmount>{credits}</CreditsAmount>
      <PlusIcon>+</PlusIcon>
    </CreditsButton>
  );
};